import { AgGridReact } from "@ag-grid-community/react";
import { InternalTemplateDetails } from "features/internal/template/InternalTemplateDetails";
import {
  InternalTemplateEditMode,
  InternalTemplateEditModeDropdown,
  useTemplateEditModeDropdown,
} from "features/internal/template/InternalTemplateEditModeDropdown";
import {
  useInternalEventTemplateEventOrderUpdate,
  useInternalEventTemplateEventsDelete,
  useInternalEventTemplateEventTableDataRetrieveSuspense,
  useInternalEventTemplateVersionRetrieveSuspense,
} from "gen/endpoints/internal/internal";
import { EventTemplateEventOrderStatusDataRequestEventIdToOrder } from "gen/model/eventTemplateEventOrderStatusDataRequestEventIdToOrder";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableRow } from "gen/model/genericTableRow";
import { IDRowMetadata } from "gen/model/iDRowMetadata";
import { IntegerTableCellValue } from "gen/model/integerTableCellValue";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { RowSelectionState } from "shared/components/table/data/DataTableTypes";
import { useToast } from "shared/components/toast/useToast";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

const InternalTemplateReorderFormTable: React.FC<{
  setEditMode: (editMode: InternalTemplateEditMode) => void;
}> = ({ setEditMode }) => {
  const { t } = useTranslation(["common", "internal"]);
  const { showErrorToast } = useToast();
  const { templateVersionId } = useRequiredStringParams();
  const form = useForm();

  const { mutateAsync: setEventTemplateEventOrder } =
    useInternalEventTemplateEventOrderUpdate();

  const { data: tableData, refetch: refetchTableData } =
    useInternalEventTemplateEventTableDataRetrieveSuspense(templateVersionId, {
      edit_mode: "reorder",
      exclude_draft_events: false,
    });

  const tableRef = useRef<{ tableRef: AgGridReact<GenericTableRow> } | null>(
    null,
  );

  return (
    <Form
      form={form}
      onSubmit={async () => {
        const orderData: EventTemplateEventOrderStatusDataRequestEventIdToOrder =
          {};

        const allRows: GenericTableRow[] = [];

        tableRef.current?.tableRef.api.forEachNode((row) => {
          if (row.data) {
            allRows.push(row.data);
          }
        });

        const numRows = allRows.length;

        allRows.forEach((row, index) => {
          // We need to pass 0-indexed values to the backend
          const orderNum = numRows - index - 1;
          const eventId = (row.metadata as IDRowMetadata).id;

          orderData[eventId] = orderNum;
        });

        await setEventTemplateEventOrder({
          eventTemplateVersionId: templateVersionId,
          data: {
            event_id_to_order: orderData,
          },
        });
      }}
      onSuccess={async () => {
        await refetchTableData();
        setEditMode("view");
      }}
      onError={(error) => {
        showErrorToast(error);
        return true;
      }}
      successMessage={t(
        "internal:templateTabs.editTemplate.reorderEvents.success",
      )}
    >
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("internal:templateTabs.editTemplate.reorderEvents.title")}
          </TableBoxTitle>
          <DataTableBoxActions showExport={false}>
            <InternalTemplateEditModeDropdown
              editMode="reorder"
              setEditMode={setEditMode}
            />
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={tableData}
            showAllRows={true}
            rowDragManaged={true}
            ref={tableRef}
            rowDragText={(params) =>
              (
                (params.defaultTextValue as unknown as GenericTableCell)
                  .value as IntegerTableCellValue
              ).value.toString()
            }
          />
        </TableBoxBody>
      </TableBox>
      <FormFooter>
        <FormFooterActions>
          <FormCancelButton onClick={() => setEditMode("view")} />
          <FormSubmitButton>{t("common:save")}</FormSubmitButton>
        </FormFooterActions>
      </FormFooter>
    </Form>
  );
};

const InternalTemplateDeleteFormTable: React.FC<{
  setEditMode: (editMode: InternalTemplateEditMode) => void;
}> = ({ setEditMode }) => {
  const { t } = useTranslation(["common", "internal"]);
  const { showErrorToast } = useToast();
  const { templateVersionId } = useRequiredStringParams();
  const form = useForm();

  const { mutateAsync: deleteEvents } = useInternalEventTemplateEventsDelete();

  const { data: tableData, refetch: refetchTableData } =
    useInternalEventTemplateEventTableDataRetrieveSuspense(templateVersionId, {
      edit_mode: "delete",
      exclude_draft_events: false,
    });

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  return (
    <Form
      form={form}
      onSubmit={async () => {
        const event_ids_to_delete = Object.keys(rowSelection).filter(
          (id) => rowSelection[id],
        );

        await deleteEvents({
          eventTemplateVersionId: templateVersionId,
          data: {
            event_ids_to_delete,
          },
        });
      }}
      onSuccess={async () => {
        await refetchTableData();
        setEditMode("view");
      }}
      onError={(error) => {
        showErrorToast(error);
        return true;
      }}
      successMessage={t(
        "internal:templateTabs.editTemplate.deleteEvents.success",
      )}
    >
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("internal:templateTabs.editTemplate.deleteEvents.title")}
          </TableBoxTitle>
          <DataTableBoxActions showExport={false}>
            <InternalTemplateEditModeDropdown
              editMode="delete"
              setEditMode={setEditMode}
            />
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={tableData}
            showAllRows={true}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
          />
        </TableBoxBody>
      </TableBox>
      <FormFooter>
        <FormFooterActions>
          <FormCancelButton onClick={() => setEditMode("view")} />
          <FormSubmitButton>{t("common:save")}</FormSubmitButton>
        </FormFooterActions>
      </FormFooter>
    </Form>
  );
};

export const InternalTemplatePage: React.FC = () => {
  const { t } = useTranslation(["common", "internal"]);
  const { templateVersionId } = useRequiredStringParams();

  const { data: templateData } =
    useInternalEventTemplateVersionRetrieveSuspense(templateVersionId);

  const { editMode, setEditMode } = useTemplateEditModeDropdown();

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        title={templateData.name}
        helperText={templateData.description}
        backButton={{
          title: t("common:back"),
        }}
      />
      <PageBody>
        {(() => {
          switch (editMode) {
            case "reorder":
              return (
                <InternalTemplateReorderFormTable setEditMode={setEditMode} />
              );
            case "delete":
              return (
                <InternalTemplateDeleteFormTable setEditMode={setEditMode} />
              );
            default:
              return (
                <InternalTemplateDetails
                  templateVersionId={templateVersionId}
                  setEditMode={setEditMode}
                />
              );
          }
        })()}
      </PageBody>
    </PageContent>
  );
};
